import styled from "@emotion/styled";
import type { SelectProps } from "@mui/material";
import { MenuItem, Select } from "@mui/material";
import { useCallback } from "react";

import { FONT_WEIGHT_SEMI_BOLD } from "../utilities";
import type { Format } from "./colorHelpers";

const StyledColorFormatSelect = styled(Select)`
  padding: 0;
  height: 100%;
  border: 0;
  background: ${({ theme }) => theme.surface};

  &:before,
  &:hover:before {
    border-bottom: 0 !important;
  }

  .select-display {
    color: inherit;
    width: 44px;
    text-align: center;
    font-weight: ${FONT_WEIGHT_SEMI_BOLD};
    line-height: 30px;
    font-size: ${({ theme }) => theme.fontSizeSmall};
    letter-spacing: 0.2px;
    padding: 0 !important;
    height: 100%;
  }
`;

const supportedColorFormats: Format[] = ["hex", "rgb", "hsl", "name"];

export type ColorFormatSelectProps = Omit<SelectProps, "onChange" | "value" | "variant"> & {
  onChange: (format: Format) => void;
  value?: Format;
};

export const ColorFormatSelect = ({ onChange, value, ...props }: ColorFormatSelectProps) => {
  const handleChange: SelectProps["onChange"] = useCallback(
    (event: any) => {
      onChange(event.target.value);
    },
    [onChange],
  );

  return (
    <StyledColorFormatSelect
      {...props}
      onChange={handleChange}
      value={value}
      variant="filled"
      SelectDisplayProps={{ className: "select-display" }}
      IconComponent={() => null}
      data-test="color-format-select-button"
      MenuProps={{
        slotProps: {
          root: { "data-test": "select-menu-container" } as any,
          list: { "data-test": "list" } as any,
        },
      }}
    >
      {supportedColorFormats.map((colorFormat) => (
        <MenuItem key={colorFormat} value={colorFormat} data-test="item">
          {colorFormat.toUpperCase()}
        </MenuItem>
      ))}
    </StyledColorFormatSelect>
  );
};
